import { CircularProgress, Typography, useMediaQuery } from '@mui/material';
import Box, { BoxProps } from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { DocsHRResponse } from 'core/API/documents';
import { useCachedQueryData } from 'core/hooks/useCachedQueryData';

import { useDocumentContext } from 'module/documents/context';
import CommonFields from 'module/documents/tabs/CommonFields';
import { DirectDepositForm } from 'module/documents/tabs/DirectDepositForm';
import { EmployeeTippingNotice } from 'module/documents/tabs/EmployeeTippingNotice';
import { FoodReportAgreements } from 'module/documents/tabs/FoodReportAgreements';
import { FTEForm } from 'module/documents/tabs/FTEForm';
import { HumanResourceTab } from 'module/documents/tabs/HumanResourceTab';
import { IUnderstandForm } from 'module/documents/tabs/IUnderstandForm';
import { MIW4 } from 'module/documents/tabs/MIW4';
import { PCIComplianceForm } from 'module/documents/tabs/PCIComplianceForm';
import SelectedEmployeeTab from 'module/documents/tabs/SelectedEmployeeTab';

import { W2Consent } from 'module/documents/tabs/W2Consent';
import { lazy, ReactNode, Suspense, SyntheticEvent, useMemo, useState } from 'react';

import { CheckCircle } from '@mui/icons-material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useEmployeeFormData } from 'module/documents/hooks';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';

// lazy load I9Fields component
const W4Form = lazy(() => import('module/documents/tabs/W4Form'));
const I9Fields = lazy(() => import('module/documents/tabs/I9Fields'));
const I9SupplementAForm = lazy(() => import('module/documents/tabs/I9FieldsSupplementA'));
const I9SupplementBForm = lazy(() => import('module/documents/tabs/I9FieldsSupplementB'));

interface TabPanelProps extends BoxProps {
	children?: ReactNode;
	index: number;
	value: number;
}

export function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...rest } = props;
	return (
		<Box
			role="tabpanel"
			hidden={value !== index}
			id={`vertical-tabpanel-${index}`}
			aria-labelledby={`vertical-tab-${index}`}
			minHeight="50vh"
			overflow="auto"
			p={3}
			flex="1"
			{...rest}
		>
			{value === index && children}
		</Box>
	);
}

function a11yProps(index: number) {
	return {
		id: `vertical-tab-${index}`,
		'aria-controls': `vertical-tabpanel-${index}`,
	};
}

interface ITabs {
	icon?: ReactNode;
	label: string;
	component: ReactNode;
	visible?: boolean;
	disabled?: boolean;
	helperText?: string;
	id?: string;
}

// Extracted function to generate the tabs array
const generateTabs = (
	formikValues, 
	selectedEmployee: boolean,  
	isHumanResource: boolean,  
	hrDetails: DocsHRResponse,  
	loadedFormIds: Record<string, number>,
	disableTabsForNewEmployee: boolean,
	setdisableTabsForNewEmployee: React.Dispatch<React.SetStateAction<boolean>>,
	setActiveTabIndex: React.Dispatch<React.SetStateAction<number>>,
	employeeSignatureFile: File | null,
	setEmployeeSignatureFile: React.Dispatch<React.SetStateAction<File | null>>,
	employerSignatureFile: File | string | null,
	setEmployerSignatureFile: React.Dispatch<React.SetStateAction<File | string | null>>,
	submittedForms: Record<string, boolean>,
	employeeState: string,
): Array<ITabs> => {

	const disableAll =
		(isHumanResource && !selectedEmployee) || (isHumanResource && hrDetails === null);
	return [
		{
			label: 'Select employee',
			component: <SelectedEmployeeTab setdisableTabsForNewEmployee={setdisableTabsForNewEmployee} />,
			visible: isHumanResource,
			icon:
			employeeState != 'existing_employee' ? null : (
				<CheckCircle fontSize="small" color="success" />
			),
		},
		{
			label: 'Human Resource Data',
			component: <HumanResourceTab setEmployerSignatureFile={setEmployerSignatureFile} />,
			icon:
			!submittedForms?.human_resource_data_submitted ? null : (
				<CheckCircle fontSize="small" color="success" />
			),
			visible: isHumanResource,
			disabled: disableTabsForNewEmployee,
		},
		{
			label: 'Common Fields',
			component: <CommonFields />,
			visible: true,
			icon:
				!submittedForms?.common_fields_submitted ? null : (
					<CheckCircle fontSize="small" color="success" />
				),
			disabled: disableAll || disableTabsForNewEmployee,
		},
		{
			label: 'I-9 Form',
			component: <I9Fields employeeSignatureFile={employeeSignatureFile} setEmployerSignatureFile={setEmployerSignatureFile} employerSignatureFile={employerSignatureFile} />,
			visible: true,
			id: 'i9', // Assigned from the tabs list
			icon:
			!submittedForms?.i9_form_submitted ? null : (
					<CheckCircle fontSize="small" color="success" />
				),
			disabled: disableAll || disableTabsForNewEmployee,
		},
		{
			label: 'I-9 Supplement A',
			helperText: 'Enabled in I-9 Form #3a',
			component: <I9SupplementAForm employeeSignatureFile={employeeSignatureFile} />,
			disabled: !submittedForms?.i9_supplement_a_submitted && (disableAll || !formikValues.translator_assisted || disableTabsForNewEmployee),
			icon:
				!submittedForms?.i9_supplement_a_submitted ? null : (
					<CheckCircle fontSize="small" color="success" />
				),
		},
		{
			label: 'I-9 Supplement B',
			component: <I9SupplementBForm employeeSignatureFile={employeeSignatureFile} setEmployeeSignatureFile={setEmployeeSignatureFile} employerSignatureFile={employerSignatureFile}
						/>,
			visible: isHumanResource,
			disabled: !submittedForms?.i9_supplement_b_submitted && (disableAll || !formikValues.employee_rehire || disableTabsForNewEmployee),
			icon:
			!submittedForms?.i9_supplement_b_submitted ? null : (
					<CheckCircle fontSize="small" color="success" />
				),
			helperText: 'Enabled in I-9 Form #3b',
		},
		{
			label: 'W-4 Form',
			component: <W4Form employeeSignatureFile={employeeSignatureFile} />,
			visible: true,
			id: 'federal_w4', // Assigned from the tabs list
			icon:
			!submittedForms?.w4_form_submitted ? null : (
					<CheckCircle fontSize="small" color="success" />
				),
			disabled: disableAll || disableTabsForNewEmployee,
		},
		{
			label: 'Notice to Tipped Employees',
			component: <EmployeeTippingNotice employerSignatureFile={employerSignatureFile} employeeSignatureFile={employeeSignatureFile} />,
			visible: true,
			disabled: disableAll || disableTabsForNewEmployee,
			id: 'tip_credit_form', // Assigned from the tabs list
			icon:
			!submittedForms?.notice_tipped_employees_submitted ? null : (
					<CheckCircle fontSize="small" color="success" />
				),
		},
		{
			label: 'MI-W4',
			component: <MIW4 employeeSignatureFile={employeeSignatureFile} />,
			visible: true,
			id: 'state_w4_michigan', // Assigned from the tabs list
			icon:
			!submittedForms?.mi_w4_form_submitted ? null : (
					<CheckCircle fontSize="small" color="success" />
				),
			disabled: disableAll || disableTabsForNewEmployee,
		},
		{
			label: 'Food Report Agreements',
			component: <FoodReportAgreements />,
			visible: true,
			id: 'employee_food_report_agreements', // Assigned from the tabs list
			disabled: disableAll || disableTabsForNewEmployee,
			icon:
			!submittedForms?.food_report_agreements_submitted ? null : (
					<CheckCircle fontSize="small" color="success" />
				),
		},
		{
			label: 'Electronic W2 consent form',
			component: <W2Consent />,
			visible: true,
			id: 'electronic_w2_consent_form', // Assigned from the tabs list
			icon:
			!submittedForms?.w2_consent_form_submitted ? null : (
					<CheckCircle fontSize="small" color="success" />
				),
			disabled: disableAll || disableTabsForNewEmployee,
		},
		{
			label: 'PCI Compliance Acknowledgment',
			component: <PCIComplianceForm />,
			visible: true,
			id: 'pci_compliance_security_acceptable_user_agreements', // Assigned from the tabs list
			disabled: disableAll || disableTabsForNewEmployee,
			icon:
			!submittedForms?.pci_acknowledgment_submitted ? null : (
					<CheckCircle fontSize="small" color="success" />
				),
		},
		{
			label: 'Direct Deposit Form',
			component: <DirectDepositForm employeeSignatureFile={employeeSignatureFile} />,
			visible: true,
			id: 'direct_deposit', // Assigned from the tabs list
			icon:
			!submittedForms?.direct_deposit_form_submitted ? null : (
					<CheckCircle fontSize="small" color="success" />
				),
			disabled: disableAll || disableTabsForNewEmployee,
		},
		{
			label: 'Full / Variable Time Form',
			component: <FTEForm />,
			visible: true,
			id: 'fte_form', // Assigned from the tabs list
			disabled: disableAll || disableTabsForNewEmployee,
			icon:
			!submittedForms?.full_variable_time_form_submitted ? null : (
					<CheckCircle fontSize="small" color="success" />
				),
		},
		{
			label: 'I Understand Form',
			component: <IUnderstandForm />,
			visible: true,
			id: 'i_understand_form', // Assigned from the tabs list
			disabled: disableAll || disableTabsForNewEmployee,
		},
	].filter((tab) => tab.visible !== false);
};

export function CompanyRegistrationStepper() {
	const [employeeSignatureFile, setEmployeeSignatureFile] = useState<File | null>(null);
	const [employerSignatureFile, setEmployerSignatureFile] = useState<File | string | null>(null);
	const [disableTabsForNewEmployee, setdisableTabsForNewEmployee] = useState(true);

	const { formik, isEmployer, selectedEmployee, loadedFormIds, activeTabIndex, setActiveTabIndex, submittedForms, employeeState } = useDocumentContext();
	const { workplace } = useWorkplacesContext();

	useEmployeeFormData();

	const handleFormTabChange = (event: SyntheticEvent, newValue: number) => {
		setActiveTabIndex(newValue);
	};

	// Commented out - employee-form API calls not needed
	// const hrDetails = useCachedQueryData<DocsHRResponse>('employee-form/hr');
	const hrDetails = null;

	const tabs = useMemo(
		() => {
			const tabsToGenerate = generateTabs(
				formik.values,
				isEmployer,
				Boolean(selectedEmployee),
				hrDetails,
				loadedFormIds,
				disableTabsForNewEmployee,
				setdisableTabsForNewEmployee,
				setActiveTabIndex,
				employeeSignatureFile,
				setEmployeeSignatureFile,
				employerSignatureFile,
				setEmployerSignatureFile,
				submittedForms,
				employeeState
			);

			return tabsToGenerate.filter((tab) => {
				if (!tab.id) return true;
				// return workplace.approved_forms.includes(tab.id);
				return false;
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			formik.values?.translator_assisted,
			formik.values?.employee_rehire,
			selectedEmployee,
			hrDetails,
			loadedFormIds,
			disableTabsForNewEmployee,
			setActiveTabIndex,
			employeeSignatureFile,
			setEmployeeSignatureFile,
			employerSignatureFile,
			setEmployerSignatureFile,
			submittedForms,
			employeeState,
		]
	);

	return (
		<Box
			sx={{
				flexGrow: 1,
				bgcolor: 'background.paper',
				display: 'flex',
				flexDirection: 'column',
				overflowY: 'auto',
				maxHeight: '75vh',
			}}
		>
			<Tabs
				variant="scrollable"
				scrollButtons="auto"
				orientation="horizontal"
				value={activeTabIndex}
				textColor="secondary"
				indicatorColor="secondary"
				onChange={handleFormTabChange}
				aria-label="Horizontal tabs"
				sx={{ px: 2, pt: 1 }}
			>
				{tabs.map((tab, index) => (
					<Tab
						key={index}
						disabled={tab.disabled}
						label={
							<Box display="flex" flexDirection="column" position="relative" textAlign="center">
								<Box display="flex" alignItems="center" justifyContent="center" gap="8px">
									{tab.icon || null}
									{tab.label}
								</Box>
								{tab.helperText && tab.disabled && (
									<Typography fontSize="0.75rem" variant="subtitle1" sx={{ textTransform: 'none' }}>
										<strong>ⓘ</strong> {tab.helperText}
									</Typography>
								)}
							</Box>
						}
						{...a11yProps(index)}
					/>
				))}
			</Tabs>

			<Suspense
				fallback={
					<TabPanel
						value={100}
						index={100}
						sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
					>
						<CircularProgress />
					</TabPanel>
				}
			>
				{tabs.map((tab, index) => (
					<TabPanel key={index} value={activeTabIndex} index={index}>
						{tab.component}
					</TabPanel>
				))}
			</Suspense>
		</Box>
	);
}
